/**
 * Client-side helpers for the onboarding activation gate.
 *
 * A team is "activated" once its first agent run has been ingested (see
 * backend teams.activated_at). Activation is sticky and monotonic — a team that
 * is activated can never become un-activated — so once we learn a team is
 * activated we cache it for the tab session and never re-check. That keeps the
 * gate free on every subsequent navigation.
 */
import axiosInstance from 'shared/services/api/axiosInstance';

const CACHE_KEY = 'trodo_activated_team_ids';

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.map(String) : [];
  } catch {
    return [];
  }
}

/** Has this team already been confirmed activated in this tab session? */
export function isTeamKnownActivated(teamId) {
  if (teamId == null) return false;
  return readCache().includes(String(teamId));
}

/** Remember a team as activated for the rest of this tab session. */
export function markTeamActivatedLocal(teamId) {
  if (teamId == null) return;
  const set = new Set(readCache());
  set.add(String(teamId));
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify([...set]));
  } catch {
    /* storage full / disabled — non-fatal, we'll just re-check */
  }
}

/**
 * Fetch a team's activation state. Returns { activated, activatedAt }.
 * On error, reports not-activated so the gate fails safe toward the install
 * wall rather than leaking an empty dashboard.
 */
export async function fetchTeamActivation(teamId) {
  if (teamId == null) return { activated: false, activatedAt: null };
  try {
    const { data } = await axiosInstance.get(`/team/${teamId}/activation`);
    const activated = data?.activated === true;
    if (activated) markTeamActivatedLocal(teamId);
    return { activated, activatedAt: data?.activatedAt || null };
  } catch (err) {
    // 403/404/network → treat as not activated; the gate will show install.
    return { activated: false, activatedAt: null };
  }
}
